import * as React from 'react';

import cx from 'classnames';

import { GlyphName } from '@remind/r101-glyphs';
import Glyph, { GlyphSize } from 'components/Glyph';
import InputConstants from 'constants/InputConstants';
import { getPropClassesFromInstance } from 'lib/getPropClasses';
import focusNode from 'utils/focusNode';
import pickInputElementProps from 'utils/pickInputElementProps';
import { Omit } from 'utils/tsUtils';

import './styles/Input.scss';

/*
********************************
IF YOU WANT TO USE ERROR STATES AND USE VALIDATIONS, USE
    FORMELEMENT (if you need labels and warnings)
********************************
*/

export type Props = {
  button?: boolean;
  append?: any;
  glyph?: GlyphName;
  className?: string;
  noBorder?: boolean;
  // Ommitting 'size' because we use it from DefaultProps and it's already defined in the React.HTMLPropsx`
} & Partial<Omit<React.HTMLProps<HTMLInputElement>, 'size'>> &
  Partial<DefaultProps> & { 'data-test'?: string };

type DefaultProps = {
  size: 'large' | 'medium';
  width: 'full' | 'auto';
  glyphSize: GlyphSize;
  autofocus: boolean;
};

class Input extends React.Component<Props> {
  static propClasses = {
    noBorder: 'Input--noBorder',
    size: {
      large: 'Input--large',
      xlarge: 'Input-xlarge',
    },
    width: {
      auto: 'Input--auto',
      full: 'Input--full',
    },
  };

  static defaultProps: DefaultProps = {
    size: 'medium',
    glyphSize: InputConstants.GLYPH_SIZE,
    autofocus: false,
    width: 'full',
  };

  componentDidMount() {
    if (this.props.autofocus) {
      // Prevents the cursor from being placed at the start of the text
      const { value } = this.getInputNode();
      this.getInputNode().value = '';
      focusNode(this.getInputNode());
      this.getInputNode().value = value;
    }
  }

  getInputNode = (): HTMLInputElement => {
    return this.refs.input as HTMLInputElement;
  };

  focus = () => {
    this.getInputNode().focus();
  };

  renderPendedInput(inputProps: any) {
    const { append, button, glyph, glyphSize } = this.props;

    return (
      <div
        className={cx('Input--hasPend', {
          'Input--hasGlyph': glyph,
          'Input--hasAppend': append,
        })}>
        <input
          {...inputProps}
          ref="input"
          className={cx(
            'Input',
            {
              'Input--button-input': button,
              'Input--hasGlyph-input': glyph,
              'Input--hasAppend-input': append,
            },
            getPropClassesFromInstance(this),
            this.props.className
          )}
        />
        {glyph && (
          <div className="Input-pend Input-glyph">
            <Glyph
              color={button ? InputConstants.COLOR : InputConstants.GLYPH_COLOR}
              name={glyph}
              size={glyphSize}
            />
          </div>
        )}
        {append && <div className="Input-pend Input-append">{append}</div>}
      </div>
    );
  }

  render() {
    const { append, button, className, glyph } = this.props;
    const inputProps = pickInputElementProps(this.props);

    if (append || glyph) {
      return this.renderPendedInput(inputProps);
    }

    return (
      <input
        {...inputProps}
        ref="input"
        className={cx(
          'Input',
          button && 'Input--button',
          getPropClassesFromInstance(this),
          className
        )}
      />
    );
  }
}

export default Input;
